export const API_URL = "/api";
export const AUTH_URL = "/auth/discord";

const userCache = new Map();

/**
 * Do a safe fetch request that handles errors gracefully.
 * @param {string} url
 * @param {RequestInit} options
 * @returns {Promise<Response>} A promise that resolves to the fetch response.
 */
async function safeFetch(url, options = {}) {
  try {
    return await fetch(url, options);
  } catch (error) {
    return new Response(null, {
      status: 500,
      statusText: error.message || error,
    });
  }
}

/**
 * Fetch the current user's data from the server.
 * @returns {Promise<DiscordUserData|null>} A promise that resolves to the user data or null if not logged in.
 */
export async function fetchUserData() {
  const response = await safeFetch(`${API_URL}/user`);
  if (!response.ok) {
    const errorData = await response.text();
    console.error("Failed to fetch user data:", errorData);
    return null;
  }
  const data = await response.json();
  return data;
}

/**
 * Fetch the leaderboard data.
 * @returns {Promise<Array<LeaderboardData>|null>} A promise that resolves to the leaderboard data or null if not found.
 */
export async function fetchLeaderboard() {
  const response = await safeFetch(`${API_URL}/leaderboard`);
  if (!response.ok) {
    const errorData = await response.json();
    console.error("Failed to fetch leaderboard:", errorData.error);
    return [];
  }
  const data = await response.json();
  return data;
}

/**
 * Fetch the plane data.
 * @returns {Promise<Array<PlaneData>|null>} A promise that resolves to the plane data or null if not found.
 */
export async function fetchPlanes() {
  const response = await safeFetch(`${API_URL}/planes`);
  if (!response.ok) {
    const errorData = await response.json();
    console.error("Failed to fetch planes:", errorData.error);
    return [];
  }
  const data = await response.json();
  return data;
}

/**
 * Fetch the total miles flown by all users.
 * @returns {Promise<number>} A promise that resolves to the total miles or 0 if an error occurs.
 */
export async function fetchTotalMiles() {
  const response = await safeFetch(`${API_URL}/totalmiles`);
  if (!response.ok) {
    const errorData = await response.json();
    console.error("Failed to fetch total miles:", errorData.error);
    return 0;
  }
  const data = await response.json();
  return data.totalMiles || 0;
}

/**
 * Fetch the latest landings.
 * @returns {Promise<Array<LandingRateData>>} A promise that resolves to an array of landing data or an empty array if an error occurs.
 */
export async function fetchLatestLandings() {
  const response = await safeFetch(`${API_URL}/latestlandings`);
  if (!response.ok) {
    const errorData = await response.json();
    console.error("Failed to fetch latest landings:", errorData.error);
    return [];
  }
  const data = await response.json();
  return data || [];
}

/**
 * Fetch user data by user ID.
 * @param {string} userId
 * @param {boolean} [useCache=false] - Whether to use cached data if available.
 * @returns {Promise<DiscordUserData|null>} A promise that resolves to the user data or null if not found.
 */
export async function fetchUserById(userId, useCache = false) {
  if (useCache && userCache.has(userId)) {
    return userCache.get(userId);
  }

  const response = await safeFetch(`${API_URL}/user/${userId}`);
  if (!response.ok) {
    const errorData = await response.json();
    console.error(`Failed to fetch user with ID ${userId}:`, errorData.error);
    return null;
  }
  const data = await response.json();
  if (useCache) {
    userCache.set(userId, data);
  }
  return data;
}

/**
 * Fetch landings by user ID.
 * @param {string} userId
 * @returns {Promise<Array<LandingRateData>>} A promise that resolves to an array of landing data or an empty array if not found.
 */
export async function fetchLandingsByUserId(userId) {
  const response = await safeFetch(`${API_URL}/landings/${userId}`);
  if (!response.ok) {
    const errorData = await response.json();
    console.error(
      `Failed to fetch landings for user with ID ${userId}:`,
      errorData.error
    );
    return [];
  }
  const data = await response.json();
  return data || [];
}

/**
 * Fetch recent flight sessions by user ID.
 * @param {string} userId
 * @returns {Promise<Array<any>>}
 */
export async function fetchFlightSessionsByUserId(userId) {
  const response = await safeFetch(`${API_URL}/flightsessions/${userId}`);
  if (!response.ok) {
    const errorData = await response.text();
    console.error(
      `Failed to fetch flight sessions for user with ID ${userId}:`,
      errorData
    );
    return [];
  }
  const data = await response.json();
  return data || [];
}

/**
 * Fetch a specific flight session by session ID.
 * @param {string} sessionId
 * @returns {Promise<any|null>}
 */
export async function fetchFlightSessionById(sessionId) {
  const response = await safeFetch(`${API_URL}/flightsession/${sessionId}`);
  if (!response.ok) {
    const errorData = await response.text();
    console.error(
      `Failed to fetch flight session with ID ${sessionId}:`,
      errorData
    );
    return null;
  }
  const data = await response.json();
  return data;
}

/**
 * Fetch the latest flight sessions.
 * @returns {Promise<Array<FlightSessionData>>} A promise that resolves to an array of flight session data or an empty array if an error occurs.
 */
export async function fetchLatestFlights() {
  const response = await safeFetch(`${API_URL}/latestflights`);
  if (!response.ok) {
    const errorData = await response.text();
    console.error(`Failed to fetch latest flights:`, errorData);
    return [];
  }
  const data = await response.json();
  return data || [];
}
